// src/components/Nav.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home,
  Heart,
  Calendar,
  UserCircle,
  Bell,
  Sun,
  Moon,
  MessageCircle,
  Sparkles,
  Megaphone,
  LogOut,
  ChevronLeft,
  ChevronRight,
  MessageSquarePlus,
  Users,
  Settings,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PajNtaubBand from './ui/PajNtaubBand';
import LanguageSelector from './LanguageSelector';
import notificationService from '../services/notificationService';
import conversationService from '../services/conversationService';
import NotificationDropdown from './NotificationDropdown';
import { canAccessDating } from '../utils';
import friendService from '../services/friendService';

// Pills top bar — desktop non-connecté uniquement
const TOPBAR_NAV = [
  { id: 'home', path: '/', lk: 'nav.home', i: Home },
  { id: 'events', path: '/events', lk: 'nav.events', i: Calendar },
  { id: 'annonces', path: '/annonces', lk: 'nav.annonces', i: Megaphone },
];

// Sidebar desktop — sections connecté uniquement (>=1024px)
const COMMUNITY_NAV = [
  { id: 'home', path: '/', lk: 'nav.home', i: Home },
  { id: 'events', path: '/events', lk: 'nav.events', i: Calendar },
  { id: 'members', path: '/members', lk: 'nav.members', i: Users },
  { id: 'annonces', path: '/annonces', lk: 'nav.annonces', i: Megaphone },
];

const DATING_NAV = [
  { id: 'discover', path: '/discover', lk: 'nav.discover', i: Heart },
  { id: 'matches', path: '/matches', lk: 'nav.matches', i: Sparkles },
];

const PERSONAL_NAV = [
  { id: 'messages', path: '/messages', lk: 'nav.messages', i: MessageCircle },
  { id: 'profile', path: '/profile', lk: 'nav.profile', i: UserCircle },
  { id: 'settings', path: '/settings', lk: 'nav.settings', i: Settings },
];

// Onglets mobile (bottom tab bar) — 5 max, dynamique selon mode
const getMobileTabs = (user) => {
  const mode = user?.mode || 'community';
  const hasDating = mode === 'dating' || mode === 'both';
  if (hasDating) {
    return [
      { id: 'events', path: '/events', lk: 'nav.events', i: Calendar },
      { id: 'annonces', path: '/annonces', lk: 'nav.annonces', i: Megaphone },
      { id: 'discover', path: '/discover', lk: 'nav.discover', i: Heart },
      { id: 'members', path: '/members', lk: 'nav.members', i: Users },
      { id: 'messages', path: '/messages', lk: 'nav.messages', i: MessageCircle },
    ];
  }
  return [
    { id: 'events', path: '/events', lk: 'nav.events', i: Calendar },
    { id: 'annonces', path: '/annonces', lk: 'nav.annonces', i: Megaphone },
    { id: 'members', path: '/members', lk: 'nav.members', i: Users },
    { id: 'messages', path: '/messages', lk: 'nav.messages', i: MessageCircle },
    { id: 'settings', path: '/settings', lk: 'nav.settings', i: Settings },
  ];
};

const SECTION_LABEL =
  'text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 px-4 pt-4 pb-1';

const Nav = ({ user, openAuth, dark, toggleDark, onLogout, sidebarCollapsed, onToggleSidebar }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const matchCount = user?.matches?.length || 0;
  const isDating = canAccessDating(user);

  // Active state : exact match pour "/", startsWith pour le reste
  const isActive = (path) => {
    if (path === '/') return currentPath === '/';
    return currentPath === path || currentPath.startsWith(path + '/');
  };

  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [msgUnreadCount, setMsgUnreadCount] = useState(0);
  const [friendReqCount, setFriendReqCount] = useState(0);
  const lastNotifTs = useRef(null);
  const notifAudio = useRef(null);

  // Son de notification (fichier audio inline)
  const playNotifSound = useCallback(() => {
    try {
      if (!notifAudio.current) {
        notifAudio.current = new Audio(
          'data:audio/wav;base64,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'
        );
        notifAudio.current.volume = 0.3;
      }
      notifAudio.current.currentTime = 0;
      notifAudio.current.play().catch(() => {
        // Audio non supporté (autoplay policy)
      });
    } catch (_err) {
      // Audio non supporté
    }
  }, []);

  // Écouter les notifications en temps réel
  useEffect(() => {
    if (!user?.uid) return;
    // Helper : extraire millis d'un Firestore Timestamp
    const getTs = (n) => {
      const t = n.createdAt;
      if (!t) return 0;
      if (typeof t.toMillis === 'function') return t.toMillis();
      if (t.seconds) return t.seconds * 1000;
      return 0;
    };

    const unsub = notificationService.subscribe(
      user.uid,
      ({ notifications: notifs, unreadCount: count }) => {
        // Détection de nouvelles notifications message par timestamp
        if (lastNotifTs.current !== null) {
          const newMsgNotifs = notifs.filter(
            (n) => n.type === 'message' && !n.read && getTs(n) > lastNotifTs.current
          );
          if (newMsgNotifs.length > 0) {
            // Anti-fantôme : pas de son si on est dans la conversation concernée
            const inConv = newMsgNotifs.every(
              (n) => n.conversationId && currentPath === `/messages/${n.conversationId}`
            );
            if (!inConv) playNotifSound();
          }
        }
        // Mettre à jour le timestamp max
        const maxTs = notifs.reduce((max, n) => Math.max(max, getTs(n)), 0);
        if (maxTs > 0) lastNotifTs.current = maxTs;
        setNotifications(notifs);
        setUnreadCount(count);
      }
    );
    return unsub;
  }, [user?.uid, currentPath, playNotifSound]);

  // Compteur non-lus dans le titre de l'onglet
  useEffect(() => {
    const total = unreadCount + msgUnreadCount;
    document.title = total > 0 ? `(${total}) HmongHearts` : 'HmongHearts';
  }, [unreadCount, msgUnreadCount]);

  // Écouter les conversations pour le badge messages non-lus
  useEffect(() => {
    if (!user?.uid) return;
    const unsub = conversationService.subscribeToConversations(
      user.uid,
      ({ unreadCount: count }) => {
        setMsgUnreadCount(count);
      }
    );
    return unsub;
  }, [user?.uid]);

  // Écouter les demandes d'amis en attente pour le badge Membres
  useEffect(() => {
    if (!user?.uid) return;
    const unsub = friendService.subscribeToPendingRequests(user.uid, (requests) => {
      setFriendReqCount(requests.length);
    });
    return unsub;
  }, [user?.uid]);

  const handleMarkRead = useCallback(async (id) => {
    try {
      await notificationService.markAsRead(id);
    } catch (err) {
      console.error('Erreur markAsRead:', err);
    }
  }, []);

  const handleMarkAllRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead(notifications);
    } catch (err) {
      console.error('Erreur markAllAsRead:', err);
    }
  }, [notifications]);

  // Fermer le menu utilisateur au clic extérieur
  useEffect(() => {
    function handleClickOutside(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    }
    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showUserMenu]);

  const handleNav = (item) => {
    if (user || item.id === 'home' || item.id === 'events' || item.id === 'annonces') {
      navigate(item.path);
    } else {
      openAuth();
    }
  };

  const handleMobileNav = (tab) => {
    if (user) {
      navigate(tab.path);
    } else {
      openAuth();
    }
  };

  // Rendu d'un item de navigation sidebar
  const renderNavItem = (item) => {
    const active = isActive(item.path);
    return (
      <button
        key={item.id}
        onClick={() => navigate(item.path)}
        data-testid={item.id === 'annonces' ? 'nav-annonces' : undefined}
        title={sidebarCollapsed ? t(item.lk) : undefined}
        className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors relative ${
          active
            ? 'bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400'
            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'
        }`}
      >
        <item.i size={20} className="flex-shrink-0" />
        {!sidebarCollapsed && t(item.lk)}
        {!sidebarCollapsed && item.id === 'matches' && matchCount > 0 && (
          <span className="ml-auto w-2.5 h-2.5 rounded-full bg-gradient-to-r from-rose-500 to-fuchsia-500" />
        )}
        {item.id === 'matches' && sidebarCollapsed && matchCount > 0 && (
          <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-gradient-to-r from-rose-500 to-fuchsia-500" />
        )}
        {!sidebarCollapsed && item.id === 'messages' && msgUnreadCount > 0 && (
          <span className="ml-auto bg-rose-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
            {msgUnreadCount > 9 ? '9+' : msgUnreadCount}
          </span>
        )}
        {sidebarCollapsed && item.id === 'messages' && msgUnreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-rose-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
            {msgUnreadCount > 9 ? '9+' : msgUnreadCount}
          </span>
        )}
        {!sidebarCollapsed && item.id === 'members' && friendReqCount > 0 && (
          <span className="ml-auto bg-violet-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
            {friendReqCount > 9 ? '9+' : friendReqCount}
          </span>
        )}
        {sidebarCollapsed && item.id === 'members' && friendReqCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-violet-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
            {friendReqCount > 9 ? '9+' : friendReqCount}
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      {/* ── Bande broderie + Barre de navigation (top) ── */}
      <div className="fixed top-0 w-full z-40">
        <PajNtaubBand />
        <nav className="bg-white/95 dark:bg-gray-950/95 backdrop-blur-xl border-b dark:border-gray-800">
          <div className="container mx-auto px-4 h-14 sm:h-16 flex justify-between items-center">
            {/* Logo */}
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <img src="/logo-hmong.svg" alt="HmongHearts" className="w-9 h-9" />
              <span
                className="text-xl font-semibold"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                <span className="text-gray-800 dark:text-gray-200">Hmong</span>
                <span className="text-rose-500 italic">Hearts</span>
              </span>
            </div>

            {/* Nav pills desktop — visible >= 1024px, non-connecté uniquement */}
            {!user && (
              <div className="hidden lg:flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-full">
                {TOPBAR_NAV.map((x) => (
                  <button
                    key={x.id}
                    onClick={() => handleNav(x)}
                    data-testid={x.id === 'annonces' ? 'nav-annonces' : undefined}
                    className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 ${
                      isActive(x.path)
                        ? 'bg-white dark:bg-gray-700 text-rose-600 dark:text-rose-400 shadow'
                        : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    <x.i size={16} />
                    {t(x.lk)}
                  </button>
                ))}
              </div>
            )}

            {/* Notifications + Dark mode + User / Login */}
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Cloche notifications — visible sur toutes tailles */}
              {user && (
                <div className="relative">
                  <button
                    onClick={() => setShowNotifs(!showNotifs)}
                    data-testid="nav-btn-notifs"
                    aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} non lue${unreadCount > 1 ? 's' : ''}` : ''}`}
                    className="relative p-2 text-gray-500 hover:text-rose-500 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    <Bell size={20} />
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 bg-rose-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>
                  {showNotifs && (
                    <NotificationDropdown
                      notifications={notifications}
                      onMarkRead={handleMarkRead}
                      onMarkAllRead={handleMarkAllRead}
                      onClose={() => setShowNotifs(false)}
                    />
                  )}
                </div>
              )}

              {/* Dark mode toggle — visible uniquement pour les non-connectés */}
              {!user && (
                <button
                  onClick={toggleDark}
                  data-testid="nav-btn-darkmode"
                  className="p-2 text-gray-500 hover:text-rose-500 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                  aria-label="Basculer le mode sombre"
                >
                  {dark ? <Sun size={20} /> : <Moon size={20} />}
                </button>
              )}

              {/* Sélecteur de langue — toujours visible */}
              <LanguageSelector />

              {/* User / Login */}
              {user ? (
                <div className="relative" ref={userMenuRef}>
                  <div
                    className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 pl-2 pr-3 py-1 rounded-full border dark:border-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    onClick={() => setShowUserMenu(!showUserMenu)}
                  >
                    {user.profilePicture ? (
                      <img
                        src={user.profilePicture}
                        alt={user.name}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <UserCircle size={32} className="text-gray-400" />
                    )}
                    <span className="font-semibold text-sm hidden sm:block dark:text-gray-200">
                      {user.name}
                    </span>
                  </div>
                  {showUserMenu && (
                    <div className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 z-50 overflow-hidden">
                      <button
                        onClick={() => {
                          navigate('/profile');
                          setShowUserMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <UserCircle size={18} />
                        {t('nav.profile')}
                      </button>
                      <button
                        onClick={() => {
                          navigate('/feedback');
                          setShowUserMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <MessageSquarePlus size={18} />
                        {t('footer.feedback')}
                      </button>
                      <button
                        onClick={() => {
                          navigate('/settings');
                          setShowUserMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <Settings size={18} />
                        {t('nav.settings')}
                      </button>
                      <div className="border-t border-gray-100 dark:border-gray-700" />
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          if (onLogout) onLogout();
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                      >
                        <LogOut size={18} />
                        {t('nav.logout')}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={openAuth}
                  data-testid="nav-btn-login"
                  title={t('nav.login')}
                  aria-label={t('nav.login')}
                  className="p-2 text-gray-500 hover:text-rose-500 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <UserCircle size={20} />
                </button>
              )}
            </div>
          </div>
        </nav>
      </div>

      {/* ── Sidebar desktop — visible >= 1024px, connecté uniquement ── */}
      {user && (
        <aside
          className={`hidden lg:flex fixed left-0 z-30 flex-col bg-white/95 dark:bg-gray-950/95 backdrop-blur-xl border-r dark:border-gray-800 transition-[width] duration-200 ${sidebarCollapsed ? 'w-16' : 'w-64'}`}
          style={{ top: 'calc(4rem + 7px)', height: 'calc(100vh - 4rem - 7px)' }}
        >
          {/* Bouton toggle collapse — collé en haut à droite, à cheval sur la bordure */}
          <button
            onClick={onToggleSidebar}
            data-testid="nav-sidebar-toggle"
            title={sidebarCollapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
            className="absolute -right-3 top-3 z-10 w-6 h-6 flex items-center justify-center rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 shadow-sm hover:shadow transition-all"
          >
            {sidebarCollapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
          </button>

          {/* Navigation principale — sections */}
          <nav className={`flex-1 overflow-y-auto ${sidebarCollapsed ? 'px-2' : 'px-3'}`}>
            {/* Section Communauté */}
            {!sidebarCollapsed && (
              <p data-testid="nav-section-communaute" className={SECTION_LABEL}>
                {t('nav.community')}
              </p>
            )}
            <div className="space-y-1">{COMMUNITY_NAV.map(renderNavItem)}</div>

            {/* Section Rencontres — visible uniquement si dating activé */}
            {isDating && (
              <>
                {!sidebarCollapsed && (
                  <p data-testid="nav-section-rencontres" className={SECTION_LABEL}>
                    {t('nav.dating')}
                  </p>
                )}
                <div className="space-y-1">{DATING_NAV.map(renderNavItem)}</div>
              </>
            )}

            {/* Section Personnel */}
            {!sidebarCollapsed && (
              <p data-testid="nav-section-personnel" className={SECTION_LABEL}>
                {t('nav.personal')}
              </p>
            )}
            <div className="space-y-1">{PERSONAL_NAV.map(renderNavItem)}</div>
          </nav>

          {/* Notifications + Feedback */}
          <div
            className={`border-t dark:border-gray-800 ${sidebarCollapsed ? 'px-2' : 'px-3'} py-3 space-y-1`}
          >
            <button
              onClick={() => navigate('/notifications')}
              title={sidebarCollapsed ? t('nav.notifications') : undefined}
              className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors relative ${
                isActive('/notifications')
                  ? 'bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <Bell size={20} className="flex-shrink-0" />
              {!sidebarCollapsed && t('nav.notifications')}
            </button>
            <button
              onClick={() => navigate('/feedback')}
              title={sidebarCollapsed ? t('footer.feedback') : undefined}
              className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                isActive('/feedback')
                  ? 'bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <MessageSquarePlus size={20} className="flex-shrink-0" />
              {!sidebarCollapsed && t('footer.feedback')}
            </button>
          </div>

          {/* Carte utilisateur */}
          <div className={`border-t dark:border-gray-800 ${sidebarCollapsed ? 'p-2' : 'p-4'}`}>
            <div
              className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} cursor-pointer rounded-xl p-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors`}
              onClick={() => navigate('/profile')}
              title={sidebarCollapsed ? user.name : undefined}
            >
              {user.profilePicture ? (
                <img
                  src={user.profilePicture}
                  alt={user.name}
                  className={`rounded-full object-cover flex-shrink-0 ${sidebarCollapsed ? 'w-8 h-8' : 'w-10 h-10'}`}
                />
              ) : (
                <UserCircle
                  size={sidebarCollapsed ? 32 : 40}
                  className="text-gray-400 flex-shrink-0"
                />
              )}
              {!sidebarCollapsed && (
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {user.name}
                  </p>
                  {user.clan && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.clan}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </aside>
      )}

      {/* ── Barre de navigation mobile (bottom) — visible < 1024px, connecté uniquement ── */}
      {user && (
        <div
          className="lg:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-950 border-t dark:border-gray-800 px-2 py-2 z-50"
          style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
        >
          <div className="flex justify-around">
            {getMobileTabs(user).map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleMobileNav(tab)}
                data-testid={tab.id === 'annonces' ? 'nav-annonces' : undefined}
                className={`flex flex-col items-center p-2 rounded-xl relative ${
                  isActive(tab.path)
                    ? 'text-rose-500 bg-rose-50 dark:bg-rose-900/30'
                    : 'text-gray-400'
                }`}
              >
                <tab.i size={20} className="w-5 h-5" />
                <span className="text-[10px]">{t(tab.lk)}</span>
                {/* Badge Messages : conversations non-lues */}
                {tab.id === 'messages' && msgUnreadCount > 0 && (
                  <span className="absolute -top-0.5 right-0.5 bg-rose-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                    {msgUnreadCount > 9 ? '9+' : msgUnreadCount}
                  </span>
                )}
                {/* Badge Membres : demandes d'amis en attente */}
                {tab.id === 'members' && friendReqCount > 0 && (
                  <span className="absolute -top-0.5 right-0.5 bg-violet-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                    {friendReqCount > 9 ? '9+' : friendReqCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default Nav;
